/**
 * Returns the abbreviated timezone name for a given IANA timezone at the current moment
 * (e.g. 'CET', 'CEST', 'EST'). Falls back to the raw IANA string on error.
 * @param {string} tz - IANA timezone identifier (e.g. 'Europe/Stockholm').
 * @returns {string}
 */
export const getTimezoneAbbreviation = tz => {
  try {
    return (
      new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'short' })
        .formatToParts(new Date())
        .find(p => p.type === 'timeZoneName')?.value ?? tz
    );
  } catch (_) {
    return tz;
  }
};

/**
 * Computes the UTC offset in minutes for a given IANA timezone at the current moment.
 * Uses Intl.DateTimeFormat with timeZoneName:'shortOffset' which returns "GMT±H[:MM]" strings
 * that are reliable and locale-independent.
 * @param {string} tz - IANA timezone identifier.
 * @returns {number} Offset in minutes (positive = ahead of UTC).
 */
const getOffsetMinutes = tz => {
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: tz,
    timeZoneName: 'shortOffset',
  }).formatToParts(new Date());
  const offsetStr = parts.find(p => p.type === 'timeZoneName')?.value ?? 'GMT+0';
  const match = offsetStr.match(/GMT([+-])(\d+)(?::(\d+))?/);
  if (!match) return 0;
  const sign = match[1] === '+' ? 1 : -1;
  return sign * (parseInt(match[2], 10) * 60 + parseInt(match[3] ?? '0', 10));
};

/**
 * Formats an offset in minutes as a signed "±HH:MM" string.
 * @param {number} minutes
 * @returns {string} e.g. '+02:00', '-05:30', '+00:00'
 */
const formatOffset = minutes => {
  const sign = minutes >= 0 ? '+' : '-';
  const abs = Math.abs(minutes);
  const h = String(Math.floor(abs / 60)).padStart(2, '0');
  const m = String(abs % 60).padStart(2, '0');
  return `${sign}${h}:${m}`;
};

/**
 * Returns a sorted list of timezone options for use in a Select component.
 * UTC appears first; the remainder are sorted by current UTC offset, then alphabetically.
 * Each option has the shape { value: string, label: string }.
 * @returns {{ value: string, label: string }[]}
 */
export const getTimezoneOptions = () => {
  const timeZones = Intl.supportedValuesOf('timeZone');

  const withOffsets = timeZones
    .filter(tz => tz !== 'UTC')
    .map(tz => {
      const offsetMinutes = getOffsetMinutes(tz);
      return {
        value: tz,
        label: `(UTC${formatOffset(offsetMinutes)}) ${tz}`,
        offsetMinutes,
      };
    });

  withOffsets.sort((a, b) => a.offsetMinutes - b.offsetMinutes || a.value.localeCompare(b.value));

  return [{ value: 'UTC', label: '(UTC+00:00) UTC' }, ...withOffsets];
};

/**
 * Returns the IANA timezone identifier of the user's browser.
 * @returns {string} e.g. 'Europe/Stockholm'
 */
export const getUserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
